// Change Analysis (spec §8.7) — a deterministic READING of a comparison result,
// never a second pass over the inputs. Pure: no DOM, no network, no sampling, no
// model. It takes the canonical record-set result the engines already produce and
// restates it; every number here can be derived by hand from `result` and every
// sentence is a template.
//
// Phase 1 reports what the result already knows: the record distribution, how
// often each field changed, which fields never changed, schema edits, and
// template sentences over those.
//
// Phase 2 adds the four noise heuristics spec §8.7 names — widespread change,
// timestamp-shaped values, monotonic counters, row reordering — as SUGGESTIONS a
// reader accepts, never as a verdict and never applied automatically. Each one
// carries the rule that fired, the evidence count, and the exact effect of acting
// on it (`projected`), which is computable from `changes[]` alone: ignoring a
// field removes precisely its cells, so a record stays modified iff it still has
// another changed field. Nothing here says a change is unimportant; the wording
// is "possibly routine", and the reader decides.
//
// Still NOT here, deliberately: co-change patterns, numeric outliers, identity
// churn and "internal inconsistencies" (REL-066).
//
// DENOMINATORS — stated once, because a panel that quotes a percentage without a
// denominator is worse than no panel:
//   * A logical record is counted ONCE, whatever number of its fields changed.
//     `modified` is a count of records, never of cells.
//   * `records.total`  = added + removed + modified + unchanged   (every logical
//     record on either side; a matched pair is one record, not two).
//   * `touchedPct`     = (added + removed + modified) / records.total.
//   * Field frequency  = records in which THAT field changed, over `modified` —
//     the matched records only. Added and removed records are whole-record
//     changes and contribute no field edits, so folding them in would inflate
//     every field's share against a denominator they can never reach.
//   * `untouchedFields` is scoped to fields comparable across matched records:
//     present on BOTH sides, excluding the key. A column that exists on one side
//     only is a schema edit, reported as such, and cannot be "untouched".
//   * `differences`    = added + removed + modified + columnsAdded +
//     columnsRemoved. Row reordering and the duplicate-key warning are notes
//     about the comparison, not differences, and are excluded.
import { isIgnoredPath } from './records/path.js';

const ruleCanHold = (name) => !/[,\r\n]/.test(name) && name === name.trim();

// Spec §8.7: "≥150 records or ≥50 differences". The OR is deliberate — a wide
// table with a handful of edits is still worth summarising, and below ~150
// records reading the diff directly beats reading a summary of it.
export const ANALYSIS_THRESHOLDS = Object.freeze({ minRecords: 150, minDifferences: 50 });

// Noise heuristics (spec §8.7). Every constant is named and exported so the UI can
// state the rule that fired instead of showing an unexplained badge.
export const NOISE_RULES = Object.freeze({
  // §8.7's figure: a field that changed in more than this share of the MODIFIED
  // records. Frequency is the entry ticket for the list — the shape rules below
  // only annotate a field that already qualifies. A timestamp column that moved in
  // a handful of records is not a pattern worth acting on, and listing it under a
  // heading about frequency would make the heading false.
  frequentPct: 80,
  // Below this many modified records a shape rule proves nothing: three rows that
  // happen to hold dates are not evidence of a timestamp column.
  minRecords: 5,
  // Cells inspected per field for the shape rules. Bounds the work on a 500k-row
  // dataset; a rule must hold for EVERY inspected pair, so a cap can only make the
  // heuristic more conservative, never more eager.
  sample: 200,
});

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 10000) / 100 : 0);
const num = (n) => n.toLocaleString('en-US');

/**
 * A share, printed at whatever precision makes it true. 20 of 100,005 records is
 * 0.02%, not 0%; 99,985 of them is 99.98%, not 100%. Rounding a nonzero share to
 * 0% tells the reader nothing changed, and rounding an incomplete one to 100%
 * tells them nothing is left — both are the opposite of the fact. Shares too small
 * to write at two decimals are bounded rather than rounded away.
 */
export function percentText(n, d) {
  if (!d || n === 0) return '0%';
  if (n === d) return '100%';
  const p = (n / d) * 100;
  if (p < 0.01) return '<0.01%';
  if (p > 99.99) return '>99.99%';
  return `${Math.round(p * 100) / 100}%`;
}

/**
 * Analyse a comparison result. `ignoreFields` must be the SAME list the
 * comparison ran with: ignored fields are excluded from the diff, so without it
 * an ignored field that really did change would be reported as "identical in
 * every matched record" — a claim the run cannot support.
 *
 * Returns null for anything that is not a
 * record-based result (text, prose, structural, stub) — those have no records to
 * distribute. Otherwise ALWAYS returns the analysis, including when it does not
 * meet the display threshold: whether to show it is the caller's decision, and
 * the caller needs the numbers either way to summarise a panel it already showed.
 */
export function analyzeChanges(result, { ignoreFields = [] } = {}) {
  if (!result || result.render?.kind !== 'records') return null;

  const added = result.added || 0;
  const removed = result.removed || 0;
  const modified = result.modified || 0;
  const unchanged = result.unchanged || 0;
  const total = added + removed + modified + unchanged;
  const touched = added + removed + modified;

  const columnsAdded = result.columnsAdded || 0;
  const columnsRemoved = result.columnsRemoved || 0;
  const differences = touched + columnsAdded + columnsRemoved;

  const changes = Array.isArray(result.changes) ? result.changes : [];
  const schema = {
    added: changes.filter((c) => c.kind === 'column-add').map((c) => c.name),
    removed: changes.filter((c) => c.kind === 'column-remove').map((c) => c.name),
    // Parquet reports declared column types; this is the OBSERVED type change the
    // reader found, reported as its own list rather than mixed into field edits.
    typeChanges: changes
      .filter((c) => c.kind === 'column-type')
      .map((c) => ({ name: c.name, oldType: c.oldType, newType: c.newType })),
  };

  // Field frequency: one increment per (field, modified record), so a field that
  // changed twice in one record still counts once for that record.
  const counts = new Map();
  const samples = new Map(); // field → up to NOISE_RULES.sample [old, new] pairs
  const changedSets = []; // one Set of changed field names per modified record
  for (const change of changes) {
    if (change.kind !== 'record-modify') continue;
    const seen = new Set();
    for (const cell of change.cells || []) {
      if (seen.has(cell.name)) continue;
      seen.add(cell.name);
      counts.set(cell.name, (counts.get(cell.name) || 0) + 1);
      const bucket = samples.get(cell.name) || [];
      if (bucket.length < NOISE_RULES.sample) bucket.push([cell.oldValue, cell.newValue]);
      samples.set(cell.name, bucket);
    }
    changedSets.push(seen);
  }
  const fields = [...counts.entries()]
    .map(([name, changed]) => ({ name, changed, pct: pct(changed, modified) }))
    .sort((a, b) => b.changed - a.changed || String(a.name).localeCompare(String(b.name)));

  const render = result.render || {};
  const keyColumn = render.keyColumn ?? result.keyColumn ?? null;
  const left = render.leftColumns || render.columns || [];
  const right = render.rightColumns || render.columns || [];
  // Comparable = on both sides and not the key itself. Ignored fields are not
  // comparable either: the engine never looked at them, so calling them
  // "identical in every record" would be a claim this run cannot support.
  const ignored = new Set(ignoreFields);
  const comparable = left.filter(
    (c) => right.includes(c) && c !== keyColumn && !isIgnoredPath(c, ignored),
  );
  const untouchedFields = comparable.filter((c) => !counts.has(c));

  const analysis = {
    format: result.format,
    records: { added, removed, modified, unchanged, total, touched, touchedPct: pct(touched, total) },
    differences,
    meetsThreshold: total >= ANALYSIS_THRESHOLDS.minRecords || differences >= ANALYSIS_THRESHOLDS.minDifferences,
    fields,
    modifiedRecords: modified,
    untouchedFields,
    comparableFields: comparable.length,
    schema,
    keyColumn,
    // A duplicate key sends the comparison down the positional fallback, which
    // changes what "the same record" means. Every count above inherits that, so
    // the panel has to say it rather than present the numbers as key-matched.
    positional: result.warning === 'duplicate-key',
    reordered: !!result.moved,
    // Fields the CURRENT comparison is ignoring, so the panel can offer undo for
    // each one: an ignored field leaves no trace in `changes[]`, so without this
    // list the row a reader just acted on would simply vanish with no way back.
    ignored: [...ignoreFields],
  };
  analysis.suggestions = suggest(analysis, { counts, samples, changedSets, ignored });
  // The headline of spec §8.10's "Signature 2": what the whole set of suggestions
  // would come to if accepted. Computed, not estimated — and offered, not applied.
  const actionable = analysis.suggestions.filter((s) => s.ignorable);
  analysis.projectedAll =
    actionable.length > 1
      ? projectIgnoring(
          analysis,
          changedSets,
          [...ignored, ...actionable.map((s) => s.field)],
          actionable.reduce((n, s) => n + s.changed, 0),
        )
      : null;
  if (analysis.projectedAll) {
    analysis.projectedAll.fields = actionable.map((s) => s.field);
    analysis.projectedAll.effect = effectText(analysis.projectedAll, analysis.records);
  }
  // One source: the rich form is authored, the plain strings are derived from it.
  analysis.summaryRich = summarize(analysis);
  analysis.summary = analysis.summaryRich.map((parts) => parts.map((p) => p.t).join(''));
  analysis.headline = headlineFor(analysis);
  return analysis;
}

/* ---------- noise heuristics (spec §8.7) — suggestions, never verdicts ---------- */

// ISO-8601-ish calendar date, optionally with a time and zone. Deliberately NOT
// `Date.parse`: that accepts "5" and "Aug", which would flag half the columns in a
// typical table. Deliberately NOT epoch integers either — an epoch second is
// indistinguishable from any other large number, and guessing wrong here means
// telling someone their id column is a timestamp.
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const INTEGER = /^[+-]?\d+$/;

const asText = (v) => (v === null || v === undefined ? '' : String(v));

// Every inspected pair must be a timestamp on BOTH sides. One non-matching value
// disqualifies the field: a partial match is not evidence of a timestamp column.
function looksTimestamped(pairs) {
  return pairs.every(([o, n]) => TIMESTAMP.test(asText(o)) && TIMESTAMP.test(asText(n)));
}

// Strictly increasing integers on every inspected pair. BigInt, never Number:
// Parquet INT64 and DECIMAL values arrive as exact strings, and Number() would
// silently collapse two distinct 19-digit ids into the same float — turning
// "counter went up" into a claim the data does not support.
function looksMonotonic(pairs) {
  return pairs.every(([o, n]) => {
    const a = asText(o);
    const b = asText(n);
    return INTEGER.test(a) && INTEGER.test(b) && BigInt(b) > BigInt(a);
  });
}

// What acting on this suggestion would actually do, computed exactly rather than
// estimated: ignoring a field drops precisely its cells, so a record stays
// modified iff it still has a changed field outside the ignore set. Matching uses
// isIgnoredPath, the same rule the engine applies, so a leaf-name ignore that also
// covers `nested.updated_at` is projected the way it will really behave.
function projectIgnoring(analysis, changedSets, ignoring, hiddenEdits) {
  const set = new Set(ignoring);
  let modified = 0;
  for (const fields of changedSets) {
    for (const name of fields) {
      if (!isIgnoredPath(name, set)) {
        modified++;
        break;
      }
    }
  }
  const { added, removed } = analysis.records;
  const columns = analysis.schema.added.length + analysis.schema.removed.length;
  return { modified, touched: added + removed + modified, differences: added + removed + modified + columns, hiddenEdits };
}

// What the button does, in the two units that differ: the FIELD EDITS it hides and
// the RECORDS still affected afterwards. Quoting only the record count reads as
// "this does nothing" whenever another field also changed in the same rows — the
// action did hide real edits, it just did not empty any record. These effects
// overlap between fields, so they cannot be added up; each figure below is
// computed against the full set being ignored, never summed.
function effectText(projected, records) {
  const edits = `Hides ${num(projected.hiddenEdits)} field edit${projected.hiddenEdits === 1 ? '' : 's'}`;
  if (projected.touched === 0) return `${edits}; no record would still be marked as changed.`;
  const parts = [];
  if (projected.modified === 0) {
    if (records.added) parts.push(`${num(records.added)} added`);
    if (records.removed) parts.push(`${num(records.removed)} removed`);
  }
  const tail = parts.length ? ` — ${parts.join(' and ')}` : '';
  return `${edits}; ${num(projected.touched)} affected record${projected.touched === 1 ? '' : 's'} remain${tail}.`;
}

function suggest(analysis, { counts, samples, changedSets, ignored }) {
  // Under the positional fallback there is no key at all (records-result.js sets
  // keyColumn to null), so "the same record" is an assumption, not a fact — and
  // every rule below assumes a matched pair IS the same record. A field that
  // differs in 100% of positionally-matched rows may just mean the rows are not
  // the same rows; the candidate key itself is the likeliest field to look that
  // way, and offering to ignore it would hide exactly what the duplicate-key
  // warning exists to show. So: no suggestions at all on this path (REL-067).
  if (analysis.positional) return [];
  const out = [];
  const modified = analysis.records.modified;
  for (const field of analysis.fields) {
    // The key is how records are matched; ignoring it is meaningless, and under the
    // positional fallback (where a matched pair CAN hold different keys) it would
    // hide the very thing the duplicate-key warning is about.
    if (field.name === analysis.keyColumn) continue;
    const pairs = samples.get(field.name) || [];
    // Frequency is the entry ticket, and it only means something against something
    // else: if this is the ONLY field that changed it is 100% of modified records
    // by construction, and that stays true after every accepted suggestion, so the
    // surviving signal would be listed the moment the real noise is gone (REL-067).
    if (analysis.fields.length < 2 || field.pct <= NOISE_RULES.frequentPct) continue;
    const rules = ['frequency'];
    if (field.changed >= NOISE_RULES.minRecords && pairs.length && looksTimestamped(pairs)) rules.push('timestamp');
    if (field.changed >= NOISE_RULES.minRecords && pairs.length && looksMonotonic(pairs)) rules.push('counter');

    const projected = projectIgnoring(analysis, changedSets, [...ignored, field.name], field.changed);
    out.push({
      field: field.name,
      rules,
      changed: field.changed,
      pct: field.pct,
      inspected: pairs.length,
      // Each label states a measured fact and carries its own denominator: read on
      // its own, "changed almost everywhere" is false here — these fields changed
      // in 12 of 100,005 records, and only 12 of 12 MODIFIED ones.
      labels: rules.map((r) => labelFor(r, field, modified, pairs.length)),
      // The ignore list is a comma-separated, trimmed, SINGLE-LINE text field (an
      // <input type=text> strips line breaks from its value). A name containing a
      // comma or a line break, or with edge spaces, cannot round-trip through it,
      // so one-click ignore is withheld rather than silently writing a rule that
      // means something else — or matches nothing (REL-093).
      ignorable: ruleCanHold(String(field.name)),
      projected,
      effect: effectText(projected, analysis.records),
      reason: reasonFor(rules, field, pairs.length, modified),
    });
  }
  return out.sort((a, b) => b.changed - a.changed || String(a.field).localeCompare(String(b.field)));
}

// "a", "a and b", "a, b and c", then "a, b, c and 4 others".
function list(names, cap = 3) {
  const shown = names.slice(0, cap);
  const rest = names.length - shown.length;
  const tail = rest > 0 ? `${num(rest)} other${rest === 1 ? '' : 's'}` : shown.pop();
  return shown.length ? `${shown.join(', ')} and ${tail}` : tail;
}

// A short, factual label per rule — each one carrying the population it is
// measured against, because a badge is read on its own, away from the sentence.
function labelFor(rule, field, modified, inspected) {
  if (rule === 'timestamp') return `all ${num(inspected)} inspected values are dates`;
  if (rule === 'counter') return `all ${num(inspected)} inspected values increased`;
  return `changed in ${num(field.changed)}/${num(modified)} modified records`;
}

// One sentence naming the rules that fired and the evidence behind them, so the
// reader can disagree with the heuristic instead of trusting a badge.
function reasonFor(rules, field, inspected, modified) {
  const parts = [`changed in ${num(field.changed)} of the ${num(modified)} modified records (${percentText(field.changed, modified)}), above the ${NOISE_RULES.frequentPct}% mark`];
  if (rules.includes('timestamp')) parts.push(`every one of the ${num(inspected)} inspected values is a date or timestamp on both sides`);
  if (rules.includes('counter')) parts.push(`every one of the ${num(inspected)} inspected values is an integer that only increased`);
  return `${field.name}: ${parts.join('; ')}.`;
}

// Template sentences. Field names are user data and are returned RAW — the render
// layer escapes them (src/render/analysis.js). Nothing here interpolates a value
// from the data, only names and counts.
// Findings as SEGMENTS, not sentences: the renderer needs to know which words are
// a count and which are a field name to give them weight, and it must not learn
// that by string-matching user data back out of a finished sentence. `summary` is
// derived from these, so the two can never drift.
//   { t }            plain text
//   { t, k:'strong' } a count worth the eye landing on
//   { t, k:'field' }  a field name — data, set in the mono face
function summarize(a) {
  const out = [];
  const { modified, total, touched } = a.records;
  const T = (t) => ({ t });
  const B = (t) => ({ t, k: 'strong' });
  const F = (t) => ({ t, k: 'field' });
  // "a, b and c" over segments, so each name keeps its own styling.
  const fieldList = (names, cap = 4) => {
    const shown = names.slice(0, cap);
    const rest = names.length - shown.length;
    const parts = [];
    shown.forEach((n, i) => {
      if (i) parts.push(T(i === shown.length - 1 && !rest ? ' and ' : ', '));
      parts.push(F(n));
    });
    if (rest > 0) parts.push(T(` and ${num(rest)} other${rest === 1 ? '' : 's'}`));
    return parts;
  };

  if (total === 0) out.push([T('No records to compare.')]);
  else if (touched === 0) out.push([T('No records changed — all '), B(num(total)), T(' are identical.')]);
  else if (modified > 0 && a.fields.length) {
    // Tied fields are named together: crowning one of them is an artefact of the
    // sort order, not a finding.
    const top = a.fields[0].changed;
    const tied = a.fields.filter((f) => f.changed === top).map((f) => f.name);
    const all = top === modified;
    const lead = all
      ? [T('All '), B(`${num(modified)} modified records`), T(` changed ${tied.length === 2 ? 'both ' : ''}`)]
      : [];
    if (all) out.push([...lead, ...fieldList(tied), T('.')]);
    else
      out.push([
        ...fieldList(tied),
        T(tied.length > 1 ? ' each changed in ' : ' changed in '),
        B(`${num(top)} of the ${num(modified)} modified records`),
        T(` (${percentText(top, modified)})`),
        T(tied.length > 1 ? '.' : ' — more than any other field.'),
      ]);
  }

  // What did NOT change is often the answer (spec §8.7).
  if (a.comparableFields > 0) {
    const u = a.untouchedFields.length;
    const other = a.fields.length > 0 ? 'other ' : '';
    out.push(
      u === 0
        ? [T('Every comparable field changed in at least one matched record.')]
        : [B(num(u)), T(` ${other}comparable field${u === 1 ? '' : 's'} stayed unchanged.`)],
    );
  }

  // The rest appear only when they apply, one short clause each. The frequency
  // explanation and the ignore pitch are NOT here: they belong beside the controls
  // they describe, not in the overview.
  const schemaParts = [];
  if (a.schema.added.length) schemaParts.push(`${num(a.schema.added.length)} column${a.schema.added.length === 1 ? '' : 's'} added`);
  if (a.schema.removed.length) schemaParts.push(`${num(a.schema.removed.length)} removed`);
  if (a.schema.typeChanges.length) schemaParts.push(`${num(a.schema.typeChanges.length)} type change${a.schema.typeChanges.length === 1 ? '' : 's'}`);
  if (schemaParts.length) out.push([T('Schema: '), B(schemaParts.join(', ')), T(', counted separately from record changes.')]);

  if (a.reordered) out.push([T('Row order changed, reported separately from record changes.')]);
  if (a.positional) {
    out.push([
      T('Records were matched by position because the key column has duplicate values, so these counts describe positional matching. No ignore suggestions are offered from them.'),
    ]);
  }
  return out;
}

// The one line the collapsed header must carry: how much of the data is affected.
function headlineFor(a) {
  const { total, touched } = a.records;
  if (!total) return 'No records to compare.';
  if (!touched) return `No records affected — all ${num(total)} are identical.`;
  return `${num(touched)} of ${num(total)} records affected (${percentText(touched, total)})`;
}
